export interface EdgeFunctionInvokeError {
  message?: string;
  context?: unknown;
}

interface EdgeFunctionErrorBody {
  code?: string;
  error?: string;
  message?: string;
  detail?: string;
  reasonCode?: string;
}

function pickEdgeFunctionErrorCode(source: EdgeFunctionErrorBody | null): string | null {
  if (typeof source?.code !== "string") return null;
  const code = source.code.trim();
  return code || null;
}

export function extractSupabaseEdgeFunctionErrorCode(
  data: unknown,
  error: EdgeFunctionInvokeError | null | undefined,
): string | null {
  const candidates = [
    findEdgeFunctionErrorCodeBody(error?.context),
    findEdgeFunctionErrorCodeBody(data),
  ];
  for (const candidate of candidates) {
    const code = pickEdgeFunctionErrorCode(candidate);
    if (code) return code;
  }
  return null;
}

export function extractSupabaseEdgeFunctionError(
  data: unknown,
  error: EdgeFunctionInvokeError | null | undefined,
  fallback = "Unknown edge function error",
): string {
  const candidates = [
    findEdgeFunctionErrorMessageBody(error?.context),
    findEdgeFunctionErrorMessageBody(data),
  ];

  for (const candidate of candidates) {
    const message = pickEdgeFunctionErrorMessage(candidate);
    if (message) {
      return message;
    }
  }

  if (typeof error?.context === "string" && error.context.trim().length > 0) {
    return error.context;
  }

  if (typeof error?.message === "string" && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
}

export async function extractSupabaseEdgeFunctionErrorAsync(
  data: unknown,
  error: EdgeFunctionInvokeError | null | undefined,
  fallback = "Unknown edge function error",
): Promise<string> {
  const context = error?.context;

  if (context && typeof (context as { text?: unknown }).text === "function") {
    try {
      const response = context as Response;
      const raw = typeof response.clone === "function"
        ? await response.clone().text()
        : await response.text();
      return extractSupabaseEdgeFunctionError(
        data,
        { ...error, context: raw },
        fallback,
      );
    } catch {
      // Fall back to the synchronous extraction path below.
    }
  }

  return extractSupabaseEdgeFunctionError(data, error, fallback);
}

export async function extractSupabaseEdgeFunctionErrorCodeAsync(
  data: unknown,
  error: EdgeFunctionInvokeError | null | undefined,
): Promise<string | null> {
  const context = error?.context;
  if (context && typeof (context as { text?: unknown }).text === "function") {
    try {
      const response = context as Response;
      const raw = typeof response.clone === "function"
        ? await response.clone().text()
        : await response.text();
      return extractSupabaseEdgeFunctionErrorCode(data, {
        ...error,
        context: raw,
      });
    } catch {
      // Fall through to synchronous structured extraction.
    }
  }
  return extractSupabaseEdgeFunctionErrorCode(data, error);
}

function readEdgeFunctionErrorBody(source: unknown): EdgeFunctionErrorBody | null {
  if (!source) {
    return null;
  }

  if (typeof source === "string") {
    try {
      const parsed = JSON.parse(source) as unknown;
      return readEdgeFunctionErrorBody(parsed);
    } catch {
      return {
        message: source.trim() || undefined,
      };
    }
  }

  if (typeof source !== "object") {
    return null;
  }

  return source as EdgeFunctionErrorBody;
}

function findEdgeFunctionErrorBody(
  source: unknown,
  matches: (candidate: EdgeFunctionErrorBody | null) => boolean,
  depth = 0,
): EdgeFunctionErrorBody | null {
  if (depth > 4 || !source) {
    return null;
  }

  const direct = readEdgeFunctionErrorBody(source);
  if (matches(direct)) {
    return direct;
  }

  if (!direct || typeof direct !== "object") {
    return null;
  }

  const nestedKeys = ["data", "body", "error", "response", "context"] as const;
  for (const key of nestedKeys) {
    if (!(key in direct)) continue;
    const nested = findEdgeFunctionErrorBody(
      (direct as Record<string, unknown>)[key],
      matches,
      depth + 1,
    );
    if (nested) {
      return nested;
    }
  }

  return null;
}

function findEdgeFunctionErrorMessageBody(source: unknown): EdgeFunctionErrorBody | null {
  return findEdgeFunctionErrorBody(
    source,
    (candidate) => Boolean(pickEdgeFunctionErrorMessage(candidate)),
  );
}

function findEdgeFunctionErrorCodeBody(source: unknown): EdgeFunctionErrorBody | null {
  return findEdgeFunctionErrorBody(
    source,
    (candidate) => Boolean(pickEdgeFunctionErrorCode(candidate)),
  );
}

function pickEdgeFunctionErrorMessage(source: EdgeFunctionErrorBody | null): string | null {
  if (!source) {
    return null;
  }

  const reasonSuffix =
    typeof source.reasonCode === "string" && source.reasonCode.trim().length > 0
      ? ` (${source.reasonCode.trim()})`
      : "";

  if (typeof source.message === "string" && source.message.trim().length > 0) {
    return `${source.message}${reasonSuffix}`;
  }

  if (typeof source.error === "string" && source.error.trim().length > 0) {
    return `${source.error}${reasonSuffix}`;
  }

  if (typeof source.detail === "string" && source.detail.trim().length > 0) {
    return `${source.detail}${reasonSuffix}`;
  }

  if (reasonSuffix) {
    return source.reasonCode?.trim() ?? null;
  }

  return null;
}
